import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Monitor, Globe, Apple, Chrome } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SubscribeModal from '../components/SubscribeModal';

const platforms = [
  { icon: Apple, label: 'macOS' },
  { icon: Monitor, label: 'Windows' },
  { icon: Globe, label: 'Linux' },
  { icon: Chrome, label: 'Web' },
];

export default function CTA() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section ref={sectionRef} className="relative py-24 bg-[#0a0a0f]/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`relative rounded-3xl overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a24] via-[#12121a] to-[#0a0a0f]" />

            {/* Glow effects */}
            <div
              className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(99,102,241,0.4) 0%, transparent 70%)',
                transform: 'translate(30%, -30%)',
              }}
            />
            <div
              className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full opacity-20"
              style={{
                background: 'radial-gradient(circle, rgba(6,182,212,0.4) 0%, transparent 70%)',
                transform: 'translate(-30%, 30%)',
              }}
            />

            {/* Border */}
            <div className="absolute inset-0 rounded-3xl border border-[rgba(99,102,241,0.2)]" />

            {/* Content */}
            <div className="relative z-10 px-8 py-16 md:px-16 md:py-20">
              <div className="max-w-2xl mx-auto text-center">
                <h2
                  className={`text-3xl sm:text-4xl md:text-5xl font-bold text-[#f8fafc] mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: '100ms' }}
                >
                  {t.cta.titleLine1}
                  <span className="text-gradient-purple"> {t.cta.titleLine2}</span>
                </h2>
                <p
                  className={`text-lg text-[#94a3b8] mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: '200ms' }}
                >
                  {t.cta.subtitle}
                </p>

                {/* CTA Button */}
                <div
                  className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: '300ms' }}
                >
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold text-lg hover:shadow-glow-purple hover:scale-105 transition-all duration-300 cursor-pointer"
                  >
                    {t.cta.button}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Platforms */}
                <div
                  className={`flex items-center justify-center gap-6 mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: '400ms' }}
                >
                  {platforms.map((platform, index) => {
                    const Icon = platform.icon;
                    return (
                      <div key={platform.label} className="flex items-center gap-2 text-[#94a3b8]">
                        <Icon className="w-5 h-5" />
                        <span className="text-sm hidden sm:inline">{platform.label}</span>
                        {index < platforms.length - 1 && (
                          <span className="ml-4 text-[rgba(255,255,255,0.1)]">|</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Features */}
                <div
                  className={`flex flex-wrap items-center justify-center gap-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: '500ms' }}
                >
                  {t.cta.features.map((feature) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(255,255,255,0.05)]"
                    >
                      <Check className="w-4 h-4 text-[#10b981]" />
                      <span className="text-sm text-[#f8fafc]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Modal */}
      <SubscribeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
